import { supabaseClient, useUser } from '@auth-v2';
import { useMutation, useQuery } from '@tanstack/react-query';

import { deleteTag, fetchAvailableTags, upsertTag, UpsertTagPayload } from '@data';

import { queryClient } from '../config/client';

const getUserTags = (userId = 'current') => [userId, 'tags', 'user'] as const;

export const useTags = (userId?: string) => {
    const { data: user } = useUser();
    return useQuery({
        queryKey: getUserTags(userId),
        queryFn: () => fetchAvailableTags(supabaseClient, userId ?? user!.id),
        enabled: !!user?.id,
    });
};

export const useUpsertTag = (userId?: string) => {
    const { data: user } = useUser();
    return useMutation({
        mutationFn: async ({ tagId, name }: Omit<UpsertTagPayload, 'userId'>) => {
            if (!user?.id) {
                return;
            }
            return await upsertTag(supabaseClient, { tagId, userId: userId ?? user.id, name });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: getUserTags(userId),
            });
        },
    });
};

export const useDeleteTag = (userId?: string) => {
    const { data: user } = useUser();
    return useMutation({
        mutationFn: async (tagId: number) => {
            if (!user?.id) {
                return;
            }
            await deleteTag(supabaseClient, { userId: userId ?? user.id, tagId });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: getUserTags(userId),
            });
        },
    });
};
